import dayjs from 'dayjs';
import { Config } from '#lib/constants/config.js';
import { getImageUrl } from '$global/utils/getImageUrl';
import { getSafeJsonLdScript } from '$global/utils/getSafeJsonLdScript/getSafeJsonLdScript';
import type { NewsArticlePDPPageData } from '$routes/news/article/[slug]/types';

export const getJsonLdScriptTag = (data: NewsArticlePDPPageData) => {
	const article = data.article;
	const seo = data.seo;

	if (!article) return null;

	const imageHeight = article.image?.height || 300;
	const imageWidth = article.image?.width || 300;

	const images = [
		{
			'@type': 'ImageObject',
			url: getImageUrl(article.image?.filename_disk, 'original'),
			width: imageHeight,
			height: imageWidth,
			caption: article.title,
		},
		{
			'@type': 'ImageObject',
			url: getImageUrl(article.image?.filename_disk, '2048px'),
			width: 2048,
			height: Math.floor(imageWidth * (2048 / imageHeight)),
		},
		{
			'@type': 'ImageObject',
			url: getImageUrl(article.image?.filename_disk, '1024px'),
			width: 1024,
			height: Math.floor(imageWidth * (1024 / imageHeight)),
		},
		{
			'@type': 'ImageObject',
			url: getImageUrl(article.image?.filename_disk, '512px'),
			width: 512,
			height: Math.floor(imageWidth * (512 / imageHeight)),
		},
	];

	const author = {
		'@type': 'Organization',
		'@id': `${Config.Domain}/news`,
		name: 'HENI News',
		url: `${Config.Domain}/news`,
	};

	const publishDate = dayjs(
		article.publication_date || article.date_created
	).format('YYYY-MM-DDTHH:mm:ssZ');
	const modifiedDate = article.date_updated || publishDate;

	// Extract categories for articleSection
	const categories = article.categories
		?.map((c) => c?.News_Category_id?.name)
		.filter(Boolean);

	// Build keywords array from categories, artists, and geotag
	const keywords = [
		...(categories || []),
		...(article.artist?.map((a) => a?.News_Artist_id?.name).filter(Boolean) ||
			[]),
		...(article.geo_tag?.name ? [article.geo_tag.name] : []),
		...(article.top_level_category ? [article.top_level_category] : []),
	].filter(Boolean);

	const articleUrl =
		seo?.url || `${Config.Domain}/news/article/${article.slug}`;

	const ld = {
		'@context': 'https://schema.org',
		'@type': 'NewsArticle',
		'@id': articleUrl,

		headline: article.title,
		description: article.short_description || seo?.description,
		datePublished: publishDate,
		dateModified: modifiedDate,

		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': articleUrl,
		},

		publisher: {
			'@type': 'Organization',
			'@id': `${Config.Domain}#publisher`,
			name: 'HENI',
			url: Config.Domain,
			logo: {
				'@type': 'ImageObject',
				url: `${Config.Domain}/img-svelte/heni-logo-large.jpg`,
				width: 2048,
				height: 2048,
			},
			sameAs: [
				'https://www.facebook.com/HENIGroup',
				'https://twitter.com/heni',
				'https://www.instagram.com/heni',
				'https://uk.linkedin.com/company/heni-group',
				'https://www.youtube.com/c/HENITalks',
			],
			description:
				'HENI is a technology company pioneering art markets and information, working with world-leading artists and estates.',
		},

		author,

		articleSection: categories?.[0],

		...(keywords.length > 0 && {
			keywords,
		}),

		...(images.length > 0 && {
			image: images,
		}),

		isAccessibleForFree: true,

		isPartOf: {
			'@type': ['CreativeWork', 'Periodical'],
			name: 'HENI News',
			url: `${Config.Domain}/news`,
		},

		...(article.geo_tag && {
			contentLocation: {
				'@type': 'Place',
				name: article.geo_tag.name,
			},
		}),

		inLanguage: 'en-US',

		copyrightYear: new Date(publishDate || '').getFullYear(),
		copyrightHolder: {
			'@type': 'Organization',
			name: 'HENI',
			url: Config.Domain,
		},

		...(article.top_level_category && {
			genre: article.top_level_category,
		}),

		speakable: {
			'@type': 'SpeakableSpecification',
			cssSelector: ['.article-headline', '.article-summary'],
		},

		potentialAction: [
			{
				'@type': 'ReadAction',
				target: {
					'@type': 'EntryPoint',
					urlTemplate: articleUrl,
				},
			},
		],
	};

	return getSafeJsonLdScript(ld);
};
